import _ from 'lodash';
import {
	TABLE_COLUMNS,
	SORT_ORDER,
	USER_ROLE_ADMIN,
	USER_ROLE_BASIC,
	USER_PAGE_SETTINGS_KEY,
	USER_TABLE_ALL,
	USER_ROLES,
	USER_TABLE_REDUCE_USERS,
	USER_TABLE_MANAGE_AGENTS,
	CAP_KEY
} from '../config';

export const PERMISSION_ROLE = [];
Object.keys(USER_ROLES).forEach(function (key) {
	PERMISSION_ROLE.push({ key, value: USER_ROLES[key].label, trackingName: USER_ROLES[key].trackingName });
});

const SMALL_BREAKPOINT_NAME_WIDTH = 150;
const SMALL_BREAKPOINT_EMAIL_WIDTH = 180;

/**
 * Check if the user already exists
 * @param {array} users all users in current company
 * @param {string} email user's email
 * @param {string} role The new role
 * @returns bool
 *
 * @memberof Users
 */
export const doesUserExist = (users, email, role) => {
	// If role is passed, check a user with the passed email and a role.
	if (role) {
		return users.some(user => (user.email.toLocaleLowerCase() === email.toLocaleLowerCase()
			&& (user.role.toLocaleLowerCase() === role.toLocaleLowerCase())));
	}

	return users.some(user => user.email.toLocaleLowerCase() === email.toLocaleLowerCase());
};

/**
 * Check if user is already in the invite list
 * @param {array} users users already added to invite popup
 * @param {string} email user's email
 * @returns bool
 *
 * @memberof Users
 */

export const isDuplicateEntry = (users, email, id) => {
	return users.some(user => user.email.toLocaleLowerCase() === email.toLocaleLowerCase() && user.id !== id);
};

/**
 * Return admins and members only
 * @param {array} users users in company
 * @returns array
 *
 * @memberof Users
 */

export const filterNonGuestUsers = (users) => {
	return users ? users.filter(user => [USER_ROLE_ADMIN, USER_ROLE_BASIC].includes(user.role)) : [];
};

// Maps user data from Middleware to data used by UI
export const encodeUser = (user) => {
	const { name, email, role, userId, externalIds, profilePhotoUri, inviteCounter, lastSessionTime } = user;
	return {
		name,
		email,
		trackingName: PERMISSION_ROLE.filter(item => item.key == role)[0].trackingName,
		role: PERMISSION_ROLE.filter(item => item.key == role)[0].value,
		userId,
		externalIds,
		profilePhotoUri,
		inviteCounter,
		lastSessionTime
	};
};

// Maps user data from UI to data used by Middleware
export const decodeUser = (user) => {
	const { email, role, userId, profilePhotoUri, lastSessionTime } = user;
	return {
		email,
		userId,
		trackingName: PERMISSION_ROLE.filter(item => item.value == role)[0].trackingName,
		role: PERMISSION_ROLE.filter(item => item.value == role)[0].key,
		profilePhotoUri,
		lastSessionTime
	};
};


/**
 * Sort users by key/order
*/
export const sortUsers = (users, sortKey, sortOrder) => {

	let result = users
		.sort((a, b) => {
			if (!sortKey || sortKey === TABLE_COLUMNS.FULL_NAME) {
				// sort by name ascending or decending first, then by email ascending
				return !sortOrder || sortOrder === SORT_ORDER.ASC
					?
					a.name.localeCompare(b.name) || a.email.localeCompare(b.email)
					:
					b.name.localeCompare(a.name) || a.email.localeCompare(b.email);
			
			} else if (sortKey == TABLE_COLUMNS.MLS_ID_TYPE2) {
				const firstAKey = _.get(a, 'originalAgentMlsId', '');
				const firstBKey = _.get(b, 'originalAgentMlsId', '');

				return sortOrder === SORT_ORDER.ASC
					?
					firstAKey.localeCompare(firstBKey)
					:
					firstBKey.localeCompare(firstAKey);

			} else if (sortKey == TABLE_COLUMNS.MLS_ID) {
				const firstAKey = _.get(a, 'externalIds[0]') || '';
				const firstBKey = _.get(b, 'externalIds[0]') || '';

				return sortOrder === SORT_ORDER.ASC
					?
					firstAKey.localeCompare(firstBKey)
					:
					firstBKey.localeCompare(firstAKey);
					
			} else if (sortKey == TABLE_COLUMNS.EMAIL) {

				return sortOrder === SORT_ORDER.ASC
					?
					a.email.localeCompare(b.email)
					:
					b.email.localeCompare(a.email);

			} else if (sortKey == TABLE_COLUMNS.CONNECTION_STATUS) {
				return sortOrder === SORT_ORDER.ASC
					?
					Number(b.mlsConnected) - Number(a.mlsConnected)
					:
					Number(a.mlsConnected) - Number(b.mlsConnected);

			} else if (sortKey == TABLE_COLUMNS.ROLE) {
				// sort by role ascending or decending first, then by name and email ascending
				return sortOrder === SORT_ORDER.ASC
					?
					a.role.localeCompare(b.role) || a.name.localeCompare(b.name) || a.email.localeCompare(b.email)
					:
					b.role.localeCompare(a.role) || a.name.localeCompare(b.name) || a.email.localeCompare(b.email);

			}
		});

	if (!sortKey ||
		sortKey == TABLE_COLUMNS.FULL_NAME ||
		sortKey == TABLE_COLUMNS.MLS_ID ||
		sortKey == TABLE_COLUMNS.MLS_ID_TYPE2) {
		// If Name or MLS ID is empty, it should be at the very bottom, but sorted by Email.
		result = result.sort(function (a, b) {
			const aNameEmpty = a.name.length === 0 ? 1 : 0;
			const bNameEmpty = b.name.length === 0 ? 1 : 0;
			if (aNameEmpty || bNameEmpty) {
				return aNameEmpty - bNameEmpty;
			}
		});
	}
		
	return result;
};

export const getSavedTableSettings = (settings, tableId, property) => {
	return _.get(settings, `cap[${USER_PAGE_SETTINGS_KEY}][${tableId}][${property}]`);
};

export const getTableStyleProps = (tableId, theme) => {
	
	return {
		/* no users message */
		noUsersFound: {
			width: '100%',
			textAlign: 'center',
			...(USER_TABLE_MANAGE_AGENTS === tableId && { 
				opacity: 1,
				font: '13px/19px Open Sans, sans-serif',
				fontSize: 13,
				fontStyle: 'normal',
				fontWeight: 400,
				display: 'inline',
				marginRight: 8
			})
		},
		/* table */
		table: {
			tableLayout: 'fixed',
			...(tableId === USER_TABLE_ALL && { marginBottom: 65}),
			...([USER_TABLE_MANAGE_AGENTS, USER_TABLE_REDUCE_USERS].includes(tableId) && { 
				width: 'calc(100% - 15px)',
			})
		},
		tableHeadRow: {
			backgroundColor: '#F4F4F4'
		},
		tableBodyRow: {
			'&.transtionsEnabled': {
				transitionDuration: '1s !important',
			},
			'&:nth-of-type(even)': {
				backgroundColor: '#F4F4F4',
			},
			'&:hover': {
				backgroundColor: '#e8e8e8 !important'
			}
		},
		tableHasSortArrow: {
			position: 'relative',
			paddingLeft: 52
		},
		tableCellHeader: {
			fontSize: 14
		},
		tableNameCellHeader: {
			...(tableId === USER_TABLE_ALL && {
				width: 184,
			}),
			...(USER_TABLE_MANAGE_AGENTS === tableId && {
				width: 200,
				[theme.breakpoints.down('sm')]: {
					width: SMALL_BREAKPOINT_NAME_WIDTH
				}
			}),
			...(USER_TABLE_REDUCE_USERS === tableId && {
				width: 230,
				[theme.breakpoints.down('sm')]: {
					width: SMALL_BREAKPOINT_NAME_WIDTH
				}
			})
		},
		tableRoleCellHeader: {
			...(tableId === USER_TABLE_ALL && { width: 120, overflow: 'visible', paddingLeft: 28})
		},
		tableEmailCellHeader: {
			...(USER_TABLE_MANAGE_AGENTS === tableId && {
				width: 250,
				[theme.breakpoints.down('sm')]: {
					width: SMALL_BREAKPOINT_EMAIL_WIDTH
				}				
			}),
			...(USER_TABLE_REDUCE_USERS === tableId && {
				width: 270,
				[theme.breakpoints.down('sm')]: {
					width: SMALL_BREAKPOINT_EMAIL_WIDTH
				}				
			})
		},
		tableMLSCellHeader: {
		},
		tableSelectReduceActionCellHeader: {
			paddingLeft: 0,
			width: 205
		},
		tableConnectionStatusCellHeader: {
			width: 200
		},
		tableCellBody: {
			fontSize: 14,
			...(USER_TABLE_MANAGE_AGENTS === tableId && {
				height: 68
			})			
		},
		tableActionsCell: {
			width: 40
		},
		tableCellReduceAction: {
			paddingRight: '5px !important'
		},
		tableNotFoundCellBody: {
			textAlign: 'center'
		},
		noBorderedCell: {
		},
		verticalTopCell: {
			verticalAlign: 'top',
			paddingTop: 0
		},
		tablePhotoCellHeader: {
			width: 184, 
		},
		tableNameCellBody: {
			...(tableId === USER_TABLE_ALL && {
				width: 184, 
			}),			
			...(USER_TABLE_MANAGE_AGENTS === tableId && {
				width: 200, 
				[theme.breakpoints.down('sm')]: {
					width: SMALL_BREAKPOINT_NAME_WIDTH
				}				
			}),
			...(USER_TABLE_REDUCE_USERS === tableId && {
				width: 230, 
				[theme.breakpoints.down('sm')]: {
					width: SMALL_BREAKPOINT_NAME_WIDTH
				}				
			})			
		},
		tableEmailCellBody: {
			...(USER_TABLE_MANAGE_AGENTS === tableId && {
				width: 250, 
				[theme.breakpoints.down('sm')]: {
					width: SMALL_BREAKPOINT_EMAIL_WIDTH
				}				
			}),
			...(USER_TABLE_REDUCE_USERS === tableId && {
				width: 270, 
				[theme.breakpoints.down('sm')]: {
					width: SMALL_BREAKPOINT_EMAIL_WIDTH
				}				
			})			
		},
		tableRoleCellBody: {
		},
		tableSelectActionCellBody: {
			width: 205,
		},
		tableConnectionStatusCellBody: {
			width: 190
		},

		/* table head */
		tableHead: {
			paddingBottom: 15
		},
		columnTableTitle: {
			width: 'auto'
		},
		tableTitle: {
			fontSize: '15px',
			lineHeight: '20px',
			fontWeight: 600
		},
		columnTableInfo: {
			width: 'auto',
			alignItems: 'center',
			marginLeft: 10
		},
		sortArrow: {
			position: 'absolute',
			top: '50%',
			transform: 'translateY(-50%)',
			left: 18,
			opacity: 0.3,
			'&.leftAligned': {
				left: -7
			},
			'&.active': {
				opacity: 1
			},
			'&.desc': {
				transform: 'translateY(-50%) rotate(-180deg)'
			}
		},

		/* role select */
		roleSelect: {
			minWidth: 115,
			'& [class*=MuiSelect-selectMenu]': {
				fontSize: 14
			}
		},
		roleSelectItems: {
			'& li': {
				fontSize: 14
			}		
		},

		/* MLS field */
		emptySelfStartMlsId: {
			cursor: 'pointer',
			padding: '11px 7px',
			maxWidth: 185,
			borderRadius: 5,
			marginLeft: '-6px',
			'&:hover': {
				background: '#e5eef3',
			}
		},

		nonEmptySelfStartMlsId: {
			cursor: 'pointer',
			padding: '4px 7px',
			maxWidth: 185,
			borderRadius: 5,
			minHeight: 40,
			display: 'flex',
			flexDirection: 'column',
			justifyContent: 'center',
			marginLeft: '-6px',
			'&:hover': {
				background: '#e5eef3',
			}
		},		
		noConnectionSelfStartMlsId: {
			padding: '4px 7px',
			maxWidth: 185,
			borderRadius: 5,
			minHeight: 40,
			display: 'flex',
			flexDirection: 'column',
			justifyContent: 'center',
			marginLeft: '-6px'
		},

		errorSelfStartMlsId: {
			color: theme.palette.error.main,
			fontSize: 14,
			lineHeight: 1.5,
			opacity: 1
		},		

		loadingSelfStartMlsId: {
			textAlign: 'left',
			'& svg': {
				marginLeft: 0
			}
		},

		/* MLS field */ 
		emptyExternalMlsId: {
			color: theme.palette.colors.colorWithAlpha(theme.palette.colors.zeta, 0.5),
			paddingTop: 8,
			minHeight: 38,
			font: '13px/19px Open Sans, sans-serif',
			'&:not(:first-child)': {
				marginTop: 10
			}
		},
		updatingMlsIdNode: {
			maxWidth: 370,
			paddingTop: 8,
			minHeight: 38,
			font: '13px/19px Open Sans, sans-serif',
			'&:not(:first-child)': {
				marginTop: 10
			}
		},
		nonEditableMlsIdValueNode: {
			maxWidth: 370,
			whiteSpace: 'nowrap',
			overflow: 'hidden',
			textOverflow: 'ellipsis',
			paddingLeft: 6,
			paddingTop: 8,
			minHeight: 38,
			font: '13px/19px Open Sans, sans-serif',
			'&:not(:first-child)': {
				marginTop: 10
			}
		},
		tableMLSCellHeaderLabel: {
			display: 'inline',
			marginRight: 2
		},

		/* tooltip content in table cell */
		tooltipList: {
			margin: '0 5px',
		},
		tooltipListItem: {
			textAlign: 'left',
			fontSize: 12,
			lineHeight: 1.5,
			position: 'relative',
			padding: '0 0 0 10px',
			'&:before': {
				content: '""',
				display: 'inline-block',
				width: '3px',
				height: '3px',
				borderRadius: 3,
				background: '#fff',
				position: 'absolute',
				top: 7,
				left: 0
			},
		},
		/* info tooltip for header cell */
		infoIconButton: {
			position: 'relative',
			width: 16,
			height: 16,
			margin: '-2px 0 0 5px',
			color: theme.palette.colors.white,
			backgroundColor: '#6F99BD',
			borderColor: '#6F99BD',
			padding: 0,
			'&:hover': {
				backgroundColor: `${theme.palette.colors.epsilonHover} !important`,
				borderColor: `${theme.palette.colors.epsilonHover} !important`
			}
		},
		tooltipArrow: {
			position: 'absolute',
			fontSize: 6,
			width: '3em',
			height: '1em',
			bottom: 0,
			left: '50%',
			transform: 'translateX(-50%)',
			marginBottom: '-0.95em',
			'&::before': {
				content: '""',
				margin: 'auto',
				display: 'block',
				width: 0,
				height: 0,
				borderStyle: 'solid',
				borderWidth: '1em 1em 0 1em',
				borderColor: `${theme.palette.common.black} transparent transparent transparent`,
			},
		},
		infoTooltip: {
			padding: 7,
			backgroundColor: theme.palette.common.black,
		},
		infoTooltipText: {
			color: theme.palette.common.white,
			opacity: 1
		},		
		infoTooltipCentered: {
			textAlign: 'center'
		},

		/* mls tooltips */
		mlsInfoTooltipTableHead: {
			padding: 7,
			backgroundColor: theme.palette.common.black,
			width: 170
		},
		mlsInfoTooltipTableBody: {
			padding: 7,
			backgroundColor: theme.palette.common.black,
			width: 140
		},

		/* add users button */
		buttonAddUsers: {
			background: theme.palette.colors.black,
			paddingLeft: 20,
			paddingRight: 20,
			...(USER_TABLE_MANAGE_AGENTS === tableId && { 
				maxWidth: 195,
				margin: '20px auto 20px 8px'
			})
		},
		iconAddUsers: {
			marginRight: 5
		},
		/* mls fields */
		mlsPendingPrompt: {
			opacity: 0.32
		},
		mlsInputField: {
			width: '100%',

			'& [id*=helper-text]': {
				padding: '0 0 10px',
				whiteSpace: 'nowrap'
			},

			margin: 0,

			'& input': {
				borderWidth: 1,
				borderStyle: 'solid',
				borderRadius: 5,
				borderColor: theme.palette.secondary.lighter,
				color: theme.palette.text.primary,
				padding: '7px 6px',
				width: '100%',

				'&:focus': {
					borderColor: theme.palette.primary.main,
				}
			}
		},
		mlsErrorMessage: {
			paddingLeft: '0 !important'
		},

		updatingMlsUserGrid: {
			flexFlow: 'row nowrap'
		},
		updatingMlsUserValue: {
			paddingRight: 10,
			paddingLeft: 6,
			width: 'calc(100% - 24px)',
			whiteSpace: 'nowrap',
			overflow: 'hidden',
			textOverflow: 'ellipsis',
		},
		updatingMlsUserIcon: {
			width: 24
		},
		updatingUserIcon: {
			display: 'block',
			height: '24px',
			width: '24px',
			margin: '0 auto',
			fill: '#bdbdbd',
			animation: 'spin 2s linear infinite'
		},
		'@keyframes spin': {
			'0': {
				transform: 'rotate(0deg)'
			},
			'100%': {
				transform: 'rotate(360deg)'
			}
		},
		singleMlsIdField: {
			maxWidth: 370,
			minHeight: 38,
			'&:not(:first-child)': {
				marginTop: 10
			}

		},
		mlsIdFieldGrid: {
			flexFlow: 'row nowrap'
		},
		mlsIdFieldWrapper: {
			width: 'calc(100% - 60px)'
		},
		mlsFieldActionIcon: {
			width: 30,
			paddingLeft: 7,
			paddingTop: 8
		},
		mlsIconDone: {
			
		},
		mlsIconAdd: {
		},
		mlsActionIcon: {
			opacity: 0.5,
			'&.mls-icon-hover:hover': {
				color: '#2888C3'
			}
		},
		mlsIconEnabled: {
			opacity: 1
		},
		/* externalIds error */
		externalIdsErrorMessage: {
			color: theme.palette.colors.error,
			opacity: 1,
			fontFamily: '"Open Sans Xara", Arial, Helvetica, sans-serif',
			fontWeight: 'normal',
			fontSize: '13px',
			lineHeight: '18px',
			whiteSpace: 'normal'
		},		
	};
};


export const sortTable = (_this, tableId, newSortColumnKey, writeUserSettings) => {
	const { sortKey: currentSortKey, sortOrder: currentSortOrder } = _this.state;
	const newState = {};

	if (!currentSortKey) {
		newState.sortKey = newSortColumnKey;
		newState.sortOrder = SORT_ORDER.DESC;
	} else if (currentSortKey === newSortColumnKey) {
		newState.sortOrder = currentSortOrder === SORT_ORDER.ASC ? SORT_ORDER.DESC : SORT_ORDER.ASC;
		newState.sortKey = currentSortKey;
	} else if (currentSortKey !== newSortColumnKey) {
		newState.sortKey = newSortColumnKey;
		newState.sortOrder = SORT_ORDER.ASC;

	}
	_this.setState(newState);

	// use same settings in all companies
	const userPageSettings = _.get(_this.props.settings,
		`${CAP_KEY}.${USER_PAGE_SETTINGS_KEY}`, {});

	userPageSettings[tableId] || (userPageSettings[tableId] = {});
	userPageSettings[tableId].sortKey = newState.sortKey;
	userPageSettings[tableId].sortOrder = newState.sortOrder;

	writeUserSettings({
		[USER_PAGE_SETTINGS_KEY]: userPageSettings
	});			
};


// WEBPACK FOOTER //
// ./src/utils/users.js